import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useWsNavigate } from "../hooks/useWsNavigate";
import { metricsAPI, aiTestingAgentAPI, observabilityAPI } from "../services/api";
import { useAgent } from "../contexts/AgentContext";
import useSetPageTitle from "../hooks/useSetPageTitle";
import { useCan } from "../hooks/useCan";
import usePagination from "../hooks/usePagination";
import Pagination from "../components/ui/Pagination";
import AgentPicker from "../components/AgentPicker";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import Button from "../components/ui/Button";
import Tabs from "../components/ui/Tabs";
import Toggle from "../components/ui/Toggle";
import GenerateMetricsModal from "../components/GenerateMetricsModal";
import { Badge } from "../components/ui/badge";
import { SearchInput } from "../components/ui/search-input";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import {
  Plus,
  Sparkles,
  Pencil,
  Trash2,
  Info,
  BarChart2,
  AlertTriangle,
  ChevronRight,
  X,
  LayoutGrid,
} from "lucide-react";
import { SplitButton } from "../components/ui/split-button";

const STANDARD_METRIC_DESCRIPTIONS = {
  latency_ms: "Time between user stops speaking and AI responds (per turn)",
  user_interruptions: "Times and locations where user spoke over AI",
  ai_interruptions: "Times and locations where AI spoke over user",
  silence_detection: "Silent periods per channel: count, duration, timestamp",
  stop_time_after_interruption: "How long the AI takes to respond after the user stops speaking (per turn)",
  transcription_accuracy: "Word Error Rate between provided and generated transcript",
  voice_change_detection: "Detects significant changes in the AI agent's voice characteristics during the conversation",
  voice_tone_clarity: "Checks clarity and tone naturalness of the AI agent's voice, scored on a scale of 1–5",
  word_per_minute: "Monitors speech speed of the AI agent to ensure natural and understandable delivery",
  false_interruption_detection: "Detects when the bot falsely reacts to noise as a user interruption — stops and restarts unnecessarily",
  interruption_non_adherence: "Detects when the user interrupts but the bot keeps talking beyond the acceptable threshold",
  logic_accuracy: "Detects factual errors, wrong calculations, and contradictions by the AI agent",
  comprehension_accuracy: "Detects when the AI agent misunderstood the user's words, intent, or responses",
};

const APPLICABLE_TO_STYLES = {
  voice: "bg-violet-50 text-violet-700 border-violet-100",
  text: "bg-teal-50 text-teal-700 border-teal-100",
  both: "bg-emerald-50 text-emerald-700 border-emerald-100",
};

const ApplicableToBadge = ({ value }) => {
  if (!value || value === "both") {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
        Voice & Text
      </span>
    );
  }
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border capitalize ${APPLICABLE_TO_STYLES[value] || ""}`}>
      {value}
    </span>
  );
};

const Metrics = () => {
  useSetPageTitle("Metrics");

  const navigate = useWsNavigate();
  const location = useLocation();
  const { currentWorkspace, user } = useAuth();
  const canWriteCustomMetric = useCan("custom_metric", "write");
  const canWriteMetricGenerator = useCan("metric_generator", "write");
  const canWriteStandardMetricPrompt = useCan("standard_metric_prompt", "write");
  const canWriteStandardMetricToggle = useCan("standard_metric_definition", "write");
  const { showToast } = useToast();
  const { agents, selectedAgent, setSelectedAgent } = useAgent();

  const METRICS_BASE = "/evaluation/metrics";
  const SUB_TABS = ["standard", "custom", "global"];
  const deriveTabs = (pathname) => {
    const segments = pathname.replace(/\/+$/, "").split("/").filter(Boolean);
    const last = segments[segments.length - 1];
    if (last === "optimization") return { topTab: "optimization", activeTab: "standard" };
    if (SUB_TABS.includes(last)) return { topTab: "overview", activeTab: last };
    return { topTab: "overview", activeTab: "standard" };
  };
  const { topTab, activeTab } = deriveTabs(location.pathname);
  const setTopTab = (tab) =>
    navigate(tab === "optimization" ? `${METRICS_BASE}/optimization` : METRICS_BASE);
  const setActiveTab = (tab) => navigate(`${METRICS_BASE}/${tab}`);

  const [promptEditModal, setPromptEditModal] = useState(null); // { metric, prompt, defaultPrompt, hasOverride }
  const [promptEditLoading, setPromptEditLoading] = useState(false);
  const [metrics, setMetrics] = useState([]);
  const [globalMetrics, setGlobalMetrics] = useState([]);
  const [standardMetrics, setStandardMetrics] = useState([]);
  const [disabledStandardMetrics, setDisabledStandardMetrics] = useState([]);
  const [disabledLlmMetricIds, setDisabledLlmMetricIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const { currentPage, totalPages, setCurrentPage, setTotalPages } = usePagination();
  const [pagination, setPagination] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [togglingKey, setTogglingKey] = useState(null);
  const [globalEditWarning, setGlobalEditWarning] = useState(null);
  const [globalDeleteWarning, setGlobalDeleteWarning] = useState(null);
  const [annotationCounts, setAnnotationCounts] = useState({});
  const [annotatedMetrics, setAnnotatedMetrics] = useState([]);
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  const fetchMetrics = useCallback(async (page = 1, clientId, search = "") => {
    try {
      setLoading(true);
      const params = { page: Number(page), client_id: clientId };
      if (selectedAgent?.id) params.agent_id = selectedAgent.id;
      if (search.trim()) params.search = search.trim();
      const response = await metricsAPI.getMetricsWithPagination(params);
      if (response && response.data) {
        setStandardMetrics(response.data.standard_metrics || []);
        setMetrics(response.data.data || []);
        setGlobalMetrics(response.data.global_metrics || []);
        setAnnotationCounts(response.data.annotation_counts || {});
        setAnnotatedMetrics(response.data.annotated_metrics || []);
        setPagination(response.data.pagination || {});
        setCurrentPage(response.data.pagination?.current_page || 1);
        setTotalPages(response.data.pagination?.total_pages || 1);
      } else {
        setMetrics([]);
        setGlobalMetrics([]);
        setStandardMetrics([]);
        setPagination({});
        setCurrentPage(1);
        setTotalPages(1);
      }
    } catch (err) {
      setError("Failed to fetch metrics");
      setMetrics([]);
      setGlobalMetrics([]);
      setStandardMetrics([]);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [selectedAgent, searchTerm]);

  const fetchAgentDisabledMetrics = useCallback(async () => {
    if (!selectedAgent?.id || !currentWorkspace?.id) {
      setDisabledStandardMetrics([]);
      setDisabledLlmMetricIds([]);
      return;
    }
    try {
      const agent = await aiTestingAgentAPI.getAgent(selectedAgent.id, currentWorkspace.id);
      const data = agent?.data || agent;
      setDisabledStandardMetrics(data?.disabled_standard_metrics || []);
      setDisabledLlmMetricIds((data?.disabled_llm_metric_ids || []).map(Number));
    } catch (err) {
      console.error("Failed to fetch agent disabled metrics:", err);
      setDisabledStandardMetrics([]);
      setDisabledLlmMetricIds([]);
    }
  }, [selectedAgent, currentWorkspace]);

  useEffect(() => {
    if (currentWorkspace?.id) fetchMetrics(currentPage, currentWorkspace.id, searchTerm);
    // eslint-disable-next-line
  }, [currentPage, currentWorkspace, selectedAgent]);

  useEffect(() => {
    if (!currentWorkspace?.id) return;
    const timer = setTimeout(() => {
      fetchMetrics(1, currentWorkspace.id, searchTerm);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [searchTerm]);

  useEffect(() => {
    fetchAgentDisabledMetrics();
  }, [fetchAgentDisabledMetrics]);

  const handleEditSystemPrompt = async (metric) => {
    if (!selectedAgent) {
      showToast("Select an agent first to edit the prompt", "error");
      return;
    }
    try {
      setPromptEditLoading(true);
      const data = await metricsAPI.getPromptOverride(currentWorkspace.id, metric.id, selectedAgent.id);
      setPromptEditModal({
        metric,
        systemPrompt: data.system_prompt || "",
        defaultSystemPrompt: data.default_system_prompt || "",
        hasOverride: data.has_override,
      });
    } catch (err) {
      console.error("Prompt override load error:", err.response?.data || err.message || err);
      showToast(err.response?.data?.error || "Failed to load prompt", "error");
    } finally {
      setPromptEditLoading(false);
    }
  };

  const handleSavePromptOverride = async () => {
    if (!promptEditModal || !selectedAgent) return;
    try {
      setPromptEditLoading(true);
      await metricsAPI.updatePromptOverride(
        currentWorkspace.id,
        promptEditModal.metric.id,
        selectedAgent.id,
        promptEditModal.systemPrompt,
      );
      showToast("Prompt saved for this agent", "success");
      setPromptEditModal(null);
    } catch (err) {
      showToast("Failed to save prompt", "error");
    } finally {
      setPromptEditLoading(false);
    }
  };

  const handleResetPromptOverride = async () => {
    if (!promptEditModal || !selectedAgent) return;
    try {
      setPromptEditLoading(true);
      await metricsAPI.deletePromptOverride(currentWorkspace.id, promptEditModal.metric.id, selectedAgent.id);
      showToast("Reset to default prompt", "success");
      setPromptEditModal(null);
    } catch (err) {
      showToast("Failed to reset prompt", "error");
    } finally {
      setPromptEditLoading(false);
    }
  };

  const handleEditMetric = (metric) => {
    if (metric.scope === "global" || metric.mark_as_global) {
      setGlobalEditWarning(metric);
    } else {
      navigate(`/evaluation/custom-metrics?edit=${metric.id}`);
    }
  };

  const confirmGlobalEdit = () => {
    if (globalEditWarning) {
      navigate(`/evaluation/custom-metrics?edit=${globalEditWarning.id}`);
      setGlobalEditWarning(null);
    }
  };

  const handleDeleteMetric = async (metric) => {
    const isGlobal = metric.scope === "global" || metric.mark_as_global;
    if (isGlobal) {
      setGlobalDeleteWarning(metric);
      return;
    }
    if (window.confirm("Are you sure you want to delete this metric?")) {
      await executeDelete(metric.id);
    }
  };

  const executeDelete = async (id) => {
    try {
      await metricsAPI.deleteMetric(id, currentWorkspace.id);
      showToast("Metric deleted successfully!", "success");
      setMetrics(metrics.filter((m) => m.id !== id));
      setGlobalMetrics(globalMetrics.filter((m) => m.id !== id));
    } catch {
      showToast("Failed to delete metric", "error");
    }
  };

  const handleToggleStandardMetric = async (metricKey) => {
    if (!selectedAgent?.id) {
      showToast("Please select an agent to configure metrics", "warning");
      return;
    }
    setTogglingKey(metricKey);
    try {
      const isDisabled = disabledStandardMetrics.includes(metricKey);
      const newDisabled = isDisabled
        ? disabledStandardMetrics.filter((k) => k !== metricKey)
        : [...disabledStandardMetrics, metricKey];
      await observabilityAPI.updateAgentStandardMetrics(currentWorkspace.id, selectedAgent.id, newDisabled);
      setDisabledStandardMetrics(newDisabled);
      showToast(isDisabled ? "Metric enabled" : "Metric disabled", "success");
    } catch {
      showToast("Failed to update metric setting", "error");
    } finally {
      setTogglingKey(null);
    }
  };

  const handleToggleLlmMetric = async (metricId) => {
    if (!selectedAgent?.id) {
      showToast("Please select an agent to configure metrics", "warning");
      return;
    }
    setTogglingKey(`llm-${metricId}`);
    try {
      const isDisabled = disabledLlmMetricIds.includes(metricId);
      const newDisabled = isDisabled
        ? disabledLlmMetricIds.filter((id) => id !== metricId)
        : [...disabledLlmMetricIds, metricId];
      await observabilityAPI.updateAgentLlmMetrics(currentWorkspace.id, selectedAgent.id, newDisabled);
      setDisabledLlmMetricIds(newDisabled);
      showToast(isDisabled ? "Metric enabled" : "Metric disabled", "success");
    } catch {
      showToast("Failed to update metric setting", "error");
    } finally {
      setTogglingKey(null);
    }
  };

  if (initialLoad) {
    return <div className="flex justify-center items-center h-full">Loading...</div>;
  }

  if (error) {
    return <div className="flex justify-center items-center h-full text-red-500">{error}</div>;
  }

  const agentMetrics = metrics;

  const totalAnnotations = Object.values(annotationCounts).reduce((a, b) => a + b, 0);

  const subTabs = [
    { id: "standard", label: "System Metrics", count: standardMetrics.length },
    { id: "custom", label: "Agent Metrics", count: pagination.total_count ?? agentMetrics.length },
    { id: "global", label: "Global Metrics", count: globalMetrics.length },
  ];

  return (
    <div className="px-4">
      {/* Top-level tabs */}
      <div className="flex space-x-1 mb-6 bg-muted rounded-lg p-1 w-fit">
        <Button
          variant="tab"
          onClick={() => setTopTab("overview")}
          className={`text-sm ${topTab === "overview" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
        >
          Metrics Overview
        </Button>
        <Button
          variant="tab"
          onClick={() => setTopTab("optimization")}
          className={`text-sm ${topTab === "optimization" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
        >
          Metrics Optimization
          {totalAnnotations > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
              {totalAnnotations}
            </span>
          )}
        </Button>
      </div>

      {/* ═══════════ OVERVIEW TAB ═══════════ */}
      {topTab === "overview" && (
        <>
          <PageHeader description="View and manage all metrics.">
            <SearchInput
              className="w-52"
              placeholder="Search metrics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {agents.length > 0 && (
              <AgentPicker
                agents={agents}
                selectedAgent={selectedAgent}
                setSelectedAgent={setSelectedAgent}
                onAdd={() => navigate("/ai-testing-agent/create")}
              />
            )}
            {canWriteMetricGenerator && (
              <Button variant="primary" onClick={() => navigate("/evaluation/generate-metrics")}>
                <Sparkles className="h-4 w-4" />
                <span>Generate by AI</span>
              </Button>
            )}
            {canWriteCustomMetric && (
              <SplitButton
                variant="primary"
                icon={Plus}
                onClick={() => navigate("/evaluation/custom-metrics")}
                items={[
                  {
                    label: "Add metrics from gallery",
                    icon: LayoutGrid,
                    onClick: () => navigate("/evaluation/metrics/gallery"),
                  },
                ]}
              >
                Create Metric
              </SplitButton>
            )}
          </PageHeader>

          {/* Sub-tabs for metric types */}
          <Tabs
            tabs={subTabs.map((t) => ({ ...t, icon: t.id === "standard" ? BarChart2 : undefined }))}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          {/* Agent warning banner */}
          {!selectedAgent && (
        <div className="mb-4 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          Select an agent above to enable or disable individual metrics.
        </div>
      )}

      {/* ── Standard Metrics Tab ── */}
      {activeTab === "standard" && (
        <div className="grid gap-3">
          {standardMetrics.map((metric) => {
            const key = metric.standard_metric_key;
            const isEnabled = !disabledStandardMetrics.includes(key);
            const isToggling = togglingKey === key;
            return (
              <div
                key={metric.id}
                className="bg-card border border-border rounded-lg px-5 py-4 flex items-center justify-between shadow-sm"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="text-sm font-medium text-foreground">{metric.name}</span>
                    <span className="text-xs font-mono text-muted-foreground">ID: {metric.id}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {STANDARD_METRIC_DESCRIPTIONS[key] || metric.metrics_type_config?.description || metric.description || ""}
                  </p>
                </div>
                <div className="flex items-center gap-3 ml-4 shrink-0">
                  {selectedAgent && (
                    <span className={`text-xs font-medium ${isEnabled ? "text-emerald-600" : "text-muted-foreground"}`}>
                      {isEnabled ? "Enabled" : "Disabled"}
                    </span>
                  )}
                  {canWriteStandardMetricPrompt && metric.analysis_type === "transcript" && !metric.is_custom && (
                    <button
                      onClick={() => navigate(`/evaluation/custom-metrics?edit=${metric.id}`)}
                      className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                      title="Edit prompt"
                    >
                      <Pencil className="h-4 w-4 text-muted-foreground" />
                    </button>
                  )}
                  {canWriteStandardMetricToggle && (
                    <Toggle
                      enabled={isEnabled}
                      onToggle={() => handleToggleStandardMetric(key)}
                      disabled={isToggling || !selectedAgent}
                      title={!selectedAgent ? "Select an agent first" : isEnabled ? "Disable metric" : "Enable metric"}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Agent Metrics Tab ── */}
      {activeTab === "custom" && (
        <>
          {agentMetrics.length === 0 ? (
            <EmptyState
              icon={Sparkles}
              title="No Agent Metrics Yet"
              description="Create your first metric or generate one using AI to get started."
              primaryAction={canWriteMetricGenerator ? { label: "Generate with AI", icon: Sparkles, onClick: () => navigate("/evaluation/generate-metrics") } : undefined}
              secondaryAction={canWriteCustomMetric ? { label: "Create Manually", icon: Plus, onClick: () => navigate("/evaluation/custom-metrics") } : undefined}
            />
          ) : (
            <div className="grid gap-3">
              {agentMetrics.map((metric) => {
                const isEnabled = !disabledLlmMetricIds.includes(metric.id);
                const isToggling = togglingKey === `llm-${metric.id}`;
                return (
                  <div
                    key={metric.id}
                    className="bg-card border border-border rounded-lg px-5 py-4 flex items-center justify-between shadow-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="text-sm font-medium text-foreground">{metric.name}</span>
                        <span className="text-xs font-mono text-muted-foreground">ID: {metric.id}</span>
                        <Badge variant="secondary" className="capitalize">
                          {metric.metrics_type?.replace(/_/g, " ")}
                        </Badge>
                        {metric.result_type && (
                          <Badge variant="success" className="capitalize">
                            {metric.result_type === "boolean" ? "Pass/Fail" : metric.result_type}
                          </Badge>
                        )}
                        {metric.is_critical && (
                          <Badge variant="danger">Critical</Badge>
                        )}
                        <ApplicableToBadge value={metric.applicable_to} />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {metric.evaluation_trigger?.type === "always"
                          ? "Always evaluated"
                          : metric.evaluation_trigger?.state_prompt || "Triggered at specific call state"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4 shrink-0">
                      {selectedAgent && (
                        <span className={`text-xs font-medium ${isEnabled ? "text-emerald-600" : "text-muted-foreground"}`}>
                          {isEnabled ? "Enabled" : "Disabled"}
                        </span>
                      )}
                      {canWriteCustomMetric && (
                        <Toggle
                          enabled={isEnabled}
                          onToggle={() => handleToggleLlmMetric(metric.id)}
                          disabled={isToggling || !selectedAgent}
                          title={!selectedAgent ? "Select an agent first" : isEnabled ? "Disable metric" : "Enable metric"}
                        />
                      )}
                      {canWriteCustomMetric && (
                        <button
                          onClick={() => handleEditMetric(metric)}
                          className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                        </button>
                      )}
                      {canWriteCustomMetric && (
                        <button
                          onClick={() => handleDeleteMetric(metric)}
                          className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </>
      )}

      {/* ── Global Metrics Tab ── */}
      {activeTab === "global" && (
        <>
          {globalMetrics.length === 0 ? (
            <EmptyState
              icon={Sparkles}
              title="No Global Metrics Yet"
              description="Global metrics run across all agents in your workspace. Create a metric and mark it as global."
              primaryAction={canWriteCustomMetric ? { label: "Create Metric", icon: Plus, onClick: () => navigate("/evaluation/custom-metrics") } : undefined}
            />
          ) : (
            <>
              <div className="mb-4 px-4 py-2 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-700">
                Global metrics are evaluated for every agent. You can disable them per agent using the toggle.
              </div>
              <div className="grid gap-3">
                {globalMetrics.map((metric) => {
                  const isEnabled = !disabledLlmMetricIds.includes(metric.id);
                  const isToggling = togglingKey === `llm-${metric.id}`;
                  return (
                    <div
                      key={metric.id}
                      className="bg-card border border-border rounded-lg px-5 py-4 flex items-center justify-between shadow-sm"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span className="text-sm font-medium text-foreground">{metric.name}</span>
                          <span className="text-xs font-mono text-muted-foreground">ID: {metric.id}</span>
                          <Badge variant="secondary" className="capitalize">
                            {metric.metrics_type?.replace(/_/g, " ")}
                          </Badge>
                          {metric.result_type && (
                            <Badge variant="success" className="capitalize">
                              {metric.result_type === "boolean" ? "Pass/Fail" : metric.result_type}
                            </Badge>
                          )}
                          {metric.is_critical && (
                            <Badge variant="danger">Critical</Badge>
                          )}
                          <ApplicableToBadge value={metric.applicable_to} />
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {metric.evaluation_trigger?.type === "always"
                            ? "Always evaluated"
                            : metric.evaluation_trigger?.state_prompt || "Triggered at specific call state"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4 shrink-0">
                        {selectedAgent && (
                          <span className={`text-xs font-medium ${isEnabled ? "text-emerald-600" : "text-muted-foreground"}`}>
                            {isEnabled ? "Enabled" : "Disabled"}
                          </span>
                        )}
                        {canWriteCustomMetric && (
                          <Toggle
                            enabled={isEnabled}
                            onToggle={() => handleToggleLlmMetric(metric.id)}
                            disabled={isToggling || !selectedAgent}
                            title={!selectedAgent ? "Select an agent first" : isEnabled ? "Disable for this agent" : "Enable for this agent"}
                          />
                        )}
                        {canWriteCustomMetric && (
                          <button
                            onClick={() => handleEditMetric(metric)}
                            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4 text-muted-foreground" />
                          </button>
                        )}
                        {canWriteCustomMetric && (
                          <button
                            onClick={() => handleDeleteMetric(metric)}
                            className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
        </>
      )}

      {/* ═══════════ OPTIMIZATION TAB ═══════════ */}
      {topTab === "optimization" && (
        <>
          {annotatedMetrics.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                No metrics with pending annotations. Mark metric results as incorrect from the call detail or run results page to start optimizing.
              </p>
            </div>
          ) : (
            <div className="grid gap-3">
              {annotatedMetrics.map((metric) => (
                <div
                  key={metric.id}
                  onClick={() => navigate(`/evaluation/metrics/${metric.id}/optimization`)}
                  className="bg-card border border-border rounded-lg px-5 py-4 flex items-center justify-between shadow-sm cursor-pointer hover:border-indigo-300 hover:shadow-md transition-all"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-sm font-medium text-foreground">{metric.name}</span>
                      <Badge variant="secondary" className="capitalize">
                        {metric.metrics_type?.replace(/_/g, " ")}
                      </Badge>
                      {metric.result_type && (
                        <Badge variant="success" className="capitalize">
                          {metric.result_type === "boolean" ? "Pass/Fail" : metric.result_type}
                        </Badge>
                      )}
                      {metric.is_critical && (
                        <Badge variant="danger">Critical</Badge>
                      )}
                      <Badge variant={metric.scope === "global" || metric.mark_as_global ? "warning" : "info"}>
                        {metric.scope === "global" || metric.mark_as_global ? "Global" : "Agent"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {annotationCounts[metric.id]} pending annotation{annotationCounts[metric.id] !== 1 ? "s" : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 ml-4 shrink-0">
                    <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">
                      {annotationCounts[metric.id]}
                    </span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Global metric edit confirmation modal */}
      {globalEditWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-card rounded-xl shadow-2xl p-4 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-foreground mb-2">Edit Global Metric</h3>
            <p className="text-sm text-muted-foreground mb-1">
              <span className="font-medium text-orange-700">"{globalEditWarning.name}"</span> is a global metric.
            </p>
            <p className="text-sm text-muted-foreground mb-5">
              Changes will affect <span className="font-semibold">all agents</span> in your workspace that use this metric. Are you sure you want to continue?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setGlobalEditWarning(null)}
                className="px-4 py-2 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted/80"
              >
                Cancel
              </button>
              <button
                onClick={confirmGlobalEdit}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700"
              >
                Edit Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global metric delete confirmation modal */}
      {globalDeleteWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-card rounded-xl shadow-2xl p-4 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-red-700 mb-2">Delete Global Metric</h3>
            <p className="text-sm text-muted-foreground mb-1">
              You are about to delete <span className="font-medium text-red-700">"{globalDeleteWarning.name}"</span>.
            </p>
            <p className="text-sm text-muted-foreground mb-5">
              This is a <span className="font-semibold">global metric</span>. Deleting it will permanently remove it from <span className="font-semibold">all agents</span> in your workspace. No agent will be evaluated on this metric going forward.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setGlobalDeleteWarning(null)}
                className="px-4 py-2 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted/80"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await executeDelete(globalDeleteWarning.id);
                  setGlobalDeleteWarning(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete from All Agents
              </button>
            </div>
          </div>
        </div>
      )}

      <GenerateMetricsModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onMetricsCreated={() => fetchMetrics(1, currentWorkspace?.id, searchTerm)}
      />

      {/* Prompt Override Modal for System LLM Metrics */}
      {promptEditModal && (
        <>
          <div className="fixed inset-0 bg-black/25 z-40" onClick={() => setPromptEditModal(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    Edit Prompt — {promptEditModal.metric.name}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Agent: {selectedAgent?.name} {promptEditModal.hasOverride && <span className="text-amber-600 font-medium">· has custom override</span>}
                  </p>
                </div>
                <button onClick={() => setPromptEditModal(null)} className="p-1.5 rounded-lg hover:bg-muted">
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                <label className="text-sm font-medium text-foreground block mb-2">System Prompt</label>
                <textarea
                  value={promptEditModal.systemPrompt}
                  onChange={(e) => setPromptEditModal({ ...promptEditModal, systemPrompt: e.target.value })}
                  className="w-full h-80 text-sm font-mono border border-input rounded-lg p-3 focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y"
                  placeholder="Enter the system prompt for this metric..."
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Template variables: {"{{metric_id}}"}, {"{{agent_rules}}"}, {"{{regulations}}"}, {"{{industry}}"}, {"{{call_purpose}}"}, {"{{transcript}}"}
                </p>
              </div>

              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <div>
                  {promptEditModal.hasOverride && (
                    <button
                      onClick={handleResetPromptOverride}
                      disabled={promptEditLoading}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Reset to Default
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setPromptEditModal(null)}
                    className="px-4 py-2 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSavePromptOverride}
                    disabled={promptEditLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
                  >
                    {promptEditLoading ? "Saving..." : "Save for this Agent"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Metrics;
